import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import TemplateDragDropEditor from './TemplateDragDropEditor';

class TemplateEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            templateConfig: {
                layout: {
                    width: 350,
                    height: 220,
                    cornerRadius: 12,
                    backgroundType: 'gradient',
                    backgroundColor: '#ffffff',
                    gradientStart: '#667eea',
                    gradientEnd: '#764ba2',
                    gradientDirection: '135deg'
                },
                elements: [
                    {
                        id: 'logo',
                        type: 'image',
                        x: 10,
                        y: 10,
                        width: 40,
                        height: 40,
                        source: '/assets/logo.png',
                        borderRadius: 50
                    },
                    {
                        id: 'title',
                        type: 'text',
                        x: 15,
                        y: 65,
                        content: 'CARTE D\'ASSURANCE SANTÉ',
                        fontSize: 14,
                        fontWeight: 'bold',
                        color: '#ffffff',
                        fontFamily: 'Arial, sans-serif'
                    },
                    {
                        id: 'name',
                        type: 'variable',
                        x: 15,
                        y: 120,
                        variable: 'nom_complet',
                        fontSize: 16,
                        fontWeight: 'bold',
                        color: '#ffffff',
                        fontFamily: 'Arial, sans-serif'
                    },
                    {
                        id: 'cardNumber',
                        type: 'variable',
                        x: 15,
                        y: 160,
                        variable: 'numero_carte',
                        fontSize: 14,
                        fontWeight: 'normal',
                        color: '#ffffff',
                        fontFamily: 'monospace',
                        letterSpacing: 2
                    },
                    {
                        id: 'photo',
                        type: 'photo',
                        x: 280,
                        y: 15,
                        width: 60,
                        height: 75,
                        borderRadius: 4,
                        border: '2px solid white'
                    }
                ]
            },
            selectedElement: null,
            previewMode: 'front',
            sampleData: {
                nom_complet: 'Jean DUPONT',
                numero_carte: '1234 5678 9012 3456',
                date_expiration: '12/2026'
            },
            availableVariables: [
                'nom', 'prenom', 'nom_complet', 'numero_carte', 'date_expiration',
                'fonction', 'entreprise', 'numero_professionnel', 'code_barre', 'qr_code'
            ],
            fontFamilies: [
                { label: 'Arial', value: 'Arial, sans-serif' },
                { label: 'Helvetica', value: 'Helvetica, sans-serif' },
                { label: 'Times', value: 'Times, serif' },
                { label: 'Courier', value: 'Courier, monospace' },
                { label: 'Georgia', value: 'Georgia, serif' }
            ],
            backgroundTypes: [
                { label: 'Couleur unie', value: 'solid' },
                { label: 'Dégradé', value: 'gradient' },
                { label: 'Image', value: 'image' }
            ]
        };
    }

    componentDidMount() {
        const { template } = this.props;
        if (template && template.metadata && template.metadata.editorConfig) {
            this.setState({
                templateConfig: template.metadata.editorConfig
            });
        }
    }

    addElement = (type) => {
        const { templateConfig } = this.state;
        const newElement = {
            id: `element_${Date.now()}`,
            type: type,
            x: 50,
            y: 50,
            width: type === 'image' || type === 'photo' ? 60 : undefined,
            height: type === 'image' || type === 'photo' ? 40 : undefined,
            content: type === 'text' ? 'Nouveau texte' : undefined,
            variable: type === 'variable' ? 'nom' : undefined,
            fontSize: type === 'text' || type === 'variable' ? 14 : undefined,
            fontWeight: 'normal',
            color: '#000000',
            fontFamily: 'Arial, sans-serif'
        };

        this.setState({
            templateConfig: {
                ...templateConfig,
                elements: [...templateConfig.elements, newElement]
            },
            selectedElement: newElement
        });
    };

    updateElement = (elementId, updates) => {
        const { templateConfig } = this.state;
        const updatedElements = templateConfig.elements.map(element =>
            element.id === elementId ? { ...element, ...updates } : element
        );

        this.setState({
            templateConfig: {
                ...templateConfig,
                elements: updatedElements
            }
        });
    };

    deleteElement = (elementId) => {
        const { templateConfig } = this.state;
        const filteredElements = templateConfig.elements.filter(element => element.id !== elementId);

        this.setState({
            templateConfig: {
                ...templateConfig,
                elements: filteredElements
            },
            selectedElement: null
        });
    };

    renderCanvas = () => {
        const { templateConfig, sampleData } = this.state;
        const { layout, elements } = templateConfig;

        const canvasStyle = {
            width: layout.width,
            height: layout.height,
            borderRadius: layout.cornerRadius,
            background: layout.backgroundType === 'gradient' ?
                `linear-gradient(${layout.gradientDirection}, ${layout.gradientStart}, ${layout.gradientEnd})` :
                layout.backgroundColor,
            position: 'relative',
            margin: '20px auto',
            border: '2px solid #ddd',
            cursor: 'crosshair',
            overflow: 'hidden'
        };

        return (
            <div style={canvasStyle}>
                {elements.map(element => this.renderElement(element))}
            </div>
        );
    };

    renderElement = (element) => {
        const { selectedElement, sampleData } = this.state;
        const isSelected = selectedElement && selectedElement.id === element.id;

        const baseStyle = {
            position: 'absolute',
            left: element.x,
            top: element.y,
            cursor: 'pointer',
            border: isSelected ? '2px dashed #007bff' : 'none',
            borderRadius: element.borderRadius || 0
        };

        const handleClick = (e) => {
            e.stopPropagation();
            this.setState({ selectedElement: element });
        };

        switch (element.type) {
            case 'text':
                return (
                    <div
                        key={element.id}
                        style={{
                            ...baseStyle,
                            fontSize: element.fontSize,
                            fontWeight: element.fontWeight,
                            color: element.color,
                            fontFamily: element.fontFamily,
                            letterSpacing: element.letterSpacing
                        }}
                        onClick={handleClick}
                    >
                        {element.content}
                    </div>
                );

            case 'variable':
                const variableValue = sampleData[element.variable] || `{${element.variable}}`;
                return (
                    <div
                        key={element.id}
                        style={{
                            ...baseStyle,
                            fontSize: element.fontSize,
                            fontWeight: element.fontWeight,
                            color: element.color,
                            fontFamily: element.fontFamily,
                            letterSpacing: element.letterSpacing
                        }}
                        onClick={handleClick}
                    >
                        {variableValue}
                    </div>
                );

            case 'image':
                return (
                    <div
                        key={element.id}
                        style={{
                            ...baseStyle,
                            width: element.width,
                            height: element.height,
                            background: element.source ? `url(${element.source})` : '#f0f0f0',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            border: element.border || (isSelected ? '2px dashed #007bff' : '1px solid #ddd')
                        }}
                        onClick={handleClick}
                    >
                        {!element.source && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                fontSize: '10px',
                                color: '#666'
                            }}>
                                IMAGE
                            </div>
                        )}
                    </div>
                );

            case 'photo':
                return (
                    <div
                        key={element.id}
                        style={{
                            ...baseStyle,
                            width: element.width,
                            height: element.height,
                            background: '#f8f9fa',
                            border: element.border || '1px solid #ddd',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '10px',
                            color: '#666'
                        }}
                        onClick={handleClick}
                    >
                        PHOTO
                    </div>
                );

            default:
                return null;
        }
    };

    renderLayoutPanel = () => {
        const { templateConfig } = this.state;
        const { layout } = templateConfig;

        return (
            <Panel header="Configuration du Layout" toggleable>
                <div className="p-fluid">
                    <Row>
                        <Col md={6}>
                            <div className="field">
                                <label>Largeur</label>
                                <InputText
                                    value={layout.width}
                                    onChange={(e) => this.updateLayout({ width: parseInt(e.target.value) })}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="field">
                                <label>Hauteur</label>
                                <InputText
                                    value={layout.height}
                                    onChange={(e) => this.updateLayout({ height: parseInt(e.target.value) })}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className="field">
                        <label>Type d'arrière-plan</label>
                        <Dropdown
                            value={layout.backgroundType}
                            options={this.state.backgroundTypes}
                            onChange={(e) => this.updateLayout({ backgroundType: e.value })}
                        />
                    </div>
                    {layout.backgroundType === 'gradient' && (
                        <Row>
                            <Col md={6}>
                                <div className="field">
                                    <label>Couleur début</label>
                                    <ColorPicker
                                        value={layout.gradientStart}
                                        onChange={(e) => this.updateLayout({ gradientStart: `#${e.value}` })}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field">
                                    <label>Couleur fin</label>
                                    <ColorPicker
                                        value={layout.gradientEnd}
                                        onChange={(e) => this.updateLayout({ gradientEnd: `#${e.value}` })}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}
                    <div className="field">
                        <label>Rayon des coins: {layout.cornerRadius}px</label>
                        <Slider
                            value={layout.cornerRadius}
                            onChange={(e) => this.updateLayout({ cornerRadius: e.value })}
                            min={0}
                            max={50}
                        />
                    </div>
                </div>
            </Panel>
        );
    };

    updateLayout = (updates) => {
        const { templateConfig } = this.state;
        this.setState({
            templateConfig: {
                ...templateConfig,
                layout: {
                    ...templateConfig.layout,
                    ...updates
                }
            }
        });
    };

    renderElementsPanel = () => {
        const { templateConfig, selectedElement } = this.state;

        return (
            <Panel header="Éléments" toggleable>
                <div className="mb-3">
                    <Button
                        label="Texte"
                        icon="pi pi-font"
                        className="p-button-sm mr-2 mb-2"
                        onClick={() => this.addElement('text')}
                    />
                    <Button
                        label="Variable"
                        icon="pi pi-tag"
                        className="p-button-sm mr-2 mb-2"
                        onClick={() => this.addElement('variable')}
                    />
                    <Button
                        label="Image"
                        icon="pi pi-image"
                        className="p-button-sm mr-2 mb-2"
                        onClick={() => this.addElement('image')}
                    />
                    <Button
                        label="Photo"
                        icon="pi pi-user"
                        className="p-button-sm mr-2 mb-2"
                        onClick={() => this.addElement('photo')}
                    />
                </div>

                <DataTable
                    value={templateConfig.elements}
                    size="small"
                    onRowClick={(e) => this.setState({ selectedElement: e.data })}
                    selectionMode="single"
                    selection={selectedElement}
                    onSelectionChange={(e) => this.setState({ selectedElement: e.value })}
                >
                    <Column field="type" header="Type" />
                    <Column
                        field="content"
                        header="Contenu"
                        body={(rowData) => rowData.content || rowData.variable || rowData.type}
                    />
                    <Column
                        body={(rowData) => (
                            <Button
                                icon="pi pi-trash"
                                className="p-button-danger p-button-text p-button-sm"
                                onClick={() => this.deleteElement(rowData.id)}
                            />
                        )}
                        style={{ width: '60px' }}
                    />
                </DataTable>
            </Panel>
        );
    };

    renderPropertiesPanel = () => {
        const { selectedElement, availableVariables, fontFamilies } = this.state;

        if (!selectedElement) {
            return (
                <Panel header="Propriétés">
                    <p className="text-muted text-center">
                        Sélectionnez un élément pour modifier ses propriétés
                    </p>
                </Panel>
            );
        }

        return (
            <Panel header={`Propriétés - ${selectedElement.type}`}>
                <div className="p-fluid">
                    <Row>
                        <Col md={6}>
                            <div className="field">
                                <label>Position X</label>
                                <InputText
                                    value={selectedElement.x}
                                    onChange={(e) => this.updateElement(selectedElement.id, { x: parseInt(e.target.value) })}
                                />
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="field">
                                <label>Position Y</label>
                                <InputText
                                    value={selectedElement.y}
                                    onChange={(e) => this.updateElement(selectedElement.id, { y: parseInt(e.target.value) })}
                                />
                            </div>
                        </Col>
                    </Row>

                    {(selectedElement.type === 'image' || selectedElement.type === 'photo') && (
                        <Row>
                            <Col md={6}>
                                <div className="field">
                                    <label>Largeur</label>
                                    <InputText
                                        value={selectedElement.width}
                                        onChange={(e) => this.updateElement(selectedElement.id, { width: parseInt(e.target.value) })}
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="field">
                                    <label>Hauteur</label>
                                    <InputText
                                        value={selectedElement.height}
                                        onChange={(e) => this.updateElement(selectedElement.id, { height: parseInt(e.target.value) })}
                                    />
                                </div>
                            </Col>
                        </Row>
                    )}

                    {selectedElement.type === 'text' && (
                        <div className="field">
                            <label>Contenu</label>
                            <InputTextarea
                                value={selectedElement.content}
                                onChange={(e) => this.updateElement(selectedElement.id, { content: e.target.value })}
                                rows={2}
                            />
                        </div>
                    )}

                    {selectedElement.type === 'variable' && (
                        <div className="field">
                            <label>Variable</label>
                            <Dropdown
                                value={selectedElement.variable}
                                options={availableVariables.map(v => ({ label: v, value: v }))}
                                onChange={(e) => this.updateElement(selectedElement.id, { variable: e.value })}
                            />
                        </div>
                    )}

                    {(selectedElement.type === 'text' || selectedElement.type === 'variable') && (
                        <>
                            <Row>
                                <Col md={6}>
                                    <div className="field">
                                        <label>Taille police</label>
                                        <InputText
                                            value={selectedElement.fontSize}
                                            onChange={(e) => this.updateElement(selectedElement.id, { fontSize: parseInt(e.target.value) })}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="field">
                                        <label>Couleur</label>
                                        <ColorPicker
                                            value={selectedElement.color}
                                            onChange={(e) => this.updateElement(selectedElement.id, { color: `#${e.value}` })}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <div className="field">
                                <label>Police</label>
                                <Dropdown
                                    value={selectedElement.fontFamily}
                                    options={fontFamilies}
                                    onChange={(e) => this.updateElement(selectedElement.id, { fontFamily: e.value })}
                                />
                            </div>
                        </>
                    )}
                </div>
            </Panel>
        );
    };

    renderPreviewPanel = () => {
        return (
            <Panel header="Prévisualisation">
                <div className="text-center">
                    {this.renderCanvas()}
                </div>
                <div className="mt-3">
                    <h6>Données d'exemple:</h6>
                    <Chips
                        value={Object.entries(this.state.sampleData)}
                        onChange={(e) => {
                            const newSampleData = {};
                            e.value.forEach(([key, value]) => {
                                newSampleData[key] = value;
                            });
                            this.setState({ sampleData: newSampleData });
                        }}
                        itemTemplate={(item) => `${item[0]}: ${item[1]}`}
                    />
                </div>
            </Panel>
        );
    };

    saveTemplate = () => {
        const { onSave, template } = this.props;
        const { templateConfig } = this.state;

        const updatedTemplate = {
            ...template,
            metadata: {
                ...template.metadata,
                editorConfig: templateConfig
            }
        };

        onSave(updatedTemplate);
    };

    render() {
        const { visible, onHide, template, onSave } = this.props;

        return (
            <TemplateDragDropEditor
                visible={visible}
                onHide={onHide}
                template={template}
                onSave={onSave}
            />
        );
    }
}

export default TemplateEditor;